import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'parentContainer',
    'parentSelect',
    'parentTypeInput',
    'resourceSelect',
    'resourceDescription',
    'resourceDescriptionText',
    'actionSelect',
    'scopeStep',
    'scopeTypeContainer',
    'scopeTypeSelect',
    'scopeFilters',
    'scopeSelect',
    'formContainer',
    'form',
    'formTitle',
    'permissionId',
    'appliesToSelect',
  ]

  connect() {
    // Store models configuration from PermissionRegistry
    this.modelsConfig = window.permissionModelsConfig || {}
    this.parentInstances = window.permissionParentInstances || {}
    this.actionLabels = window.permissionActionLabels || {}
    this.selectedModel = null
    this.selectedAction = null
    this.editingPermissionId = null
    this.currentParentOptions = null
  }

  // Scope type is now radio buttons inside the scopeTypeSelect container
  get scopeTypeValue() {
    if (!this.hasScopeTypeSelectTarget) return 'none'
    const checked = this.scopeTypeSelectTarget.querySelector(
      'input[name="permission_policy[scope_type]"]:checked'
    )
    return checked ? checked.value : 'none'
  }

  set scopeTypeValue(val) {
    if (!this.hasScopeTypeSelectTarget) return
    const radio = this.scopeTypeSelectTarget.querySelector(
      `input[name="permission_policy[scope_type]"][value="${val}"]`
    )
    if (radio) radio.checked = true
  }

  get selectedParentAgentType() {
    if (!this.hasParentSelectTarget || !this.currentParentOptions) return null
    const selectedId = this.parentSelectTarget.value
    if (!selectedId || selectedId === 'all') return null
    const parent = this.currentParentOptions.find((p) => String(p.id) === String(selectedId))
    // Mixed/unified agents handle both SDR and Support — don't filter scopes
    if (parent?.mixed_agent) return null
    return parent?.agent_type || null
  }

  showForm(event) {
    event.preventDefault()
    this.resetForm()
    this.editingPermissionId = null
    this.formTitleTarget.textContent = 'Configurar Nova Permissão'
    const form = document.getElementById('permission-form')
    if (form) {
      form.classList.remove('hidden')
    }
  }

  hideForm(event) {
    event.preventDefault()
    const form = document.getElementById('permission-form')
    if (form) {
      form.classList.add('hidden')
      this.resetForm()
      this.editingPermissionId = null
    }
  }

  editPermission(event) {
    event.preventDefault()
    const button = event.currentTarget
    const permissionId = button.dataset.permissionId
    const permissionData = JSON.parse(button.dataset.permissionData)

    this.editingPermissionId = permissionId
    this.formTitleTarget.textContent = 'Editar Permissão'

    // Set the permission ID in the hidden field
    if (this.hasPermissionIdTarget) {
      this.permissionIdTarget.value = permissionId
    }

    // Update form action to use PUT method for update
    const form = this.formTarget
    const baseUrl = form.action.replace(/\/permissions.*$/, '')
    form.action = `${baseUrl}/permissions/${permissionId}`

    // Add method override for PUT
    let methodInput = form.querySelector('input[name="_method"]')
    if (!methodInput) {
      methodInput = document.createElement('input')
      methodInput.type = 'hidden'
      methodInput.name = '_method'
      form.appendChild(methodInput)
    }
    methodInput.value = 'patch'

    // Reset scope state from any previous edit
    this.scopeTypeValue = 'none'
    this.scopeFiltersTarget.classList.add('hidden')
    this.scopeSelectTarget.innerHTML = ''

    // Populate resource type and trigger actions update
    this.resourceSelectTarget.value = permissionData.resource_type
    this.updateActions({ target: this.resourceSelectTarget })

    // Wait for actions to be populated, then set the action
    setTimeout(() => {
      this.actionSelectTarget.value = permissionData.action
      this.updateScopes({ target: this.actionSelectTarget })

      // Set parent if exists
      if (permissionData.parent_type) {
        this.parentTypeInputTarget.value = permissionData.parent_type
        if (permissionData.parent_id) {
          this.parentSelectTarget.value = permissionData.parent_id
        } else {
          // "All agents" — select the explicit "all" option
          this.parentSelectTarget.value = 'all'
        }
      }

      // Get selected scope keys (support both new scope_keys array and legacy scope_key)
      let selectedKeys = permissionData.scope_keys || []
      if (selectedKeys.length === 0 && permissionData.scope_key) {
        selectedKeys = [permissionData.scope_key]
      }

      // Extract pre-selected configurable scope values from scope_config
      const preselectedConfigValues = {}
      if (permissionData.scope_config) {
        const configurableScopes = this.selectedModel?.configurable_scopes || {}
        for (const [key, meta] of Object.entries(configurableScopes)) {
          // ui_only scopes store values directly in scope_config as { key: [values] }
          const directValues = permissionData.scope_config[key]
          if (Array.isArray(directValues) && directValues.length > 0) {
            preselectedConfigValues[key] = directValues.map(String)
            continue
          }

          // Regular scopes store values inside filters using filter_template
          const template = meta.filter_template
          if (template && permissionData.scope_config.filters) {
            const matchingFilter = permissionData.scope_config.filters.find(
              (f) => f.column === template.column && f.operator === template.operator
            )
            if (matchingFilter?.value) {
              preselectedConfigValues[key] = matchingFilter.value.map(String)
            }
          }
        }
      }

      // Set scope type — treat "filtered" with no scope_keys as effectively "none"
      // (scope_type is auto-set to filtered by the model when parent is present)
      const effectiveScopeType =
        permissionData.scope_type === 'filtered' && selectedKeys.length === 0
          ? 'none'
          : permissionData.scope_type || 'none'
      this.scopeTypeValue = effectiveScopeType
      this.toggleScopeFilters(null, selectedKeys, preselectedConfigValues)

      // Set applies-to (members vs managers only)
      if (this.hasAppliesToSelectTarget) {
        this.appliesToSelectTarget.value = permissionData.applies_to || 'all_members'
      }

      // Set enabled checkbox and update toggle visual
      const enabledCheckbox = form.querySelector('input[name="permission_policy[enabled]"]')
      if (enabledCheckbox) {
        enabledCheckbox.checked = permissionData.enabled
        // Trigger change event to update toggle-switch visual
        enabledCheckbox.dispatchEvent(new Event('change', { bubbles: true }))
      }

      // Show form and scroll into view
      this.formContainerTarget.classList.remove('hidden')
      this.formContainerTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 100)
  }

  updateActions(event) {
    const resourceType = this.resourceSelectTarget.value
    if (!resourceType) {
      this.hideParentSelector()
      this.hideResourceDescription()
      return
    }

    // Find model configuration
    const modelConfig = this.modelsConfig[resourceType]
    if (!modelConfig) {
      this.hideParentSelector()
      this.hideResourceDescription()
      return
    }

    this.selectedModel = modelConfig

    // Show resource description
    this.showResourceDescription(resourceType)

    // Handle parent selector
    if (modelConfig.parent_class) {
      this.showParentSelector(modelConfig.parent_class)
    } else {
      this.hideParentSelector()
    }

    // Clear and populate actions
    this.actionSelectTarget.innerHTML = '<option value="">Selecione a Ação</option>'

    modelConfig.actions.forEach((action) => {
      const option = document.createElement('option')
      option.value = action
      option.textContent = this.translateAction(action)
      this.actionSelectTarget.appendChild(option)
    })
  }

  showParentSelector(parentClassName) {
    if (!this.hasParentContainerTarget) return

    this.parentContainerTarget.classList.remove('hidden')

    if (this.hasParentTypeInputTarget) {
      this.parentTypeInputTarget.value = parentClassName
    }

    // Enable required validation — parent is mandatory for resources that declare one
    if (this.hasParentSelectTarget) {
      this.parentSelectTarget.required = true
    }

    // Populate parent dropdown
    this.currentParentOptions = this.parentInstances[parentClassName] || []
    this.parentSelectTarget.innerHTML = '<option value="">Selecione o Agente Virtual</option>'

    // "All agents" option with explicit value
    const allOption = document.createElement('option')
    allOption.value = 'all'
    allOption.textContent = 'Todos os Agentes Virtuais'
    this.parentSelectTarget.appendChild(allOption)

    this.currentParentOptions.forEach((parent) => {
      const option = document.createElement('option')
      option.value = parent.id
      option.textContent = parent.display_name || parent.name
      this.parentSelectTarget.appendChild(option)
    })
  }

  hideParentSelector() {
    if (!this.hasParentContainerTarget) return

    this.parentContainerTarget.classList.add('hidden')

    if (this.hasParentTypeInputTarget) {
      this.parentTypeInputTarget.value = ''
    }

    // Disable required and reset
    if (this.hasParentSelectTarget) {
      this.parentSelectTarget.required = false
      this.parentSelectTarget.selectedIndex = 0
    }
  }

  showScopeSelection() {
    if (this.hasScopeStepTarget) this.scopeStepTarget.classList.remove('hidden')
    if (this.hasScopeTypeContainerTarget) this.scopeTypeContainerTarget.classList.remove('hidden')
  }

  hideScopeSelection() {
    // Hide entire step 3 (circle + content)
    if (this.hasScopeStepTarget) this.scopeStepTarget.classList.add('hidden')

    // Auto-set scope_type to 'none' when hidden
    this.scopeTypeValue = 'none'

    // Hide scope filters as well
    if (this.hasScopeFiltersTarget) {
      this.scopeFiltersTarget.classList.add('hidden')
    }
  }

  showResourceDescription(resourceType) {
    if (!this.hasResourceDescriptionTarget || !this.hasResourceDescriptionTextTarget) return

    const description = this.getResourceDescription(resourceType)
    if (description) {
      this.resourceDescriptionTextTarget.textContent = description
      this.resourceDescriptionTarget.classList.remove('hidden')
    } else {
      this.hideResourceDescription()
    }
  }

  hideResourceDescription() {
    if (!this.hasResourceDescriptionTarget) return
    this.resourceDescriptionTarget.classList.add('hidden')
  }

  getResourceDescription(resourceType) {
    // Get description from models config (loaded from i18n)
    const modelConfig = this.modelsConfig[resourceType]
    return modelConfig?.description || ''
  }

  updateScopes(event) {
    const action = this.actionSelectTarget.value
    if (!action || !this.selectedModel) return

    this.selectedAction = action
    this.refreshScopeAvailability()
  }

  onParentChanged() {
    if (!this.selectedAction || !this.selectedModel) return

    const availableCount = this.getFilteredScopeCount()

    if (availableCount === 0) {
      this.setFilteredRadioEnabled(false)
    } else {
      this.setFilteredRadioEnabled(true)

      // Rebuild scope checkboxes if "Com Restrições" is active
      if (this.scopeTypeValue === 'filtered') {
        this.loadScopes()
      }
    }
  }

  // Re-evaluate which scopes are available (called when action or parent changes)
  refreshScopeAvailability() {
    if (!this.selectedModel) return

    const availableCount = this.getFilteredScopeCount()

    if (availableCount === 0) {
      this.hideScopeSelection()
    } else {
      this.showScopeSelection()
      this.setFilteredRadioEnabled(true)

      // Default to "filtered" for new permissions (not editing) when scopes exist
      if (!this.editingPermissionId) {
        this.scopeTypeValue = 'filtered'
        this.toggleScopeFilters()
      }
    }
  }

  getFilteredScopeCount() {
    if (!this.selectedModel?.scopes) return 0

    const agentType = this.selectedParentAgentType
    const scopeAgentTypes = this.selectedModel.scope_agent_types || {}

    return this.selectedModel.scopes.filter((scopeKey) => {
      const allowedTypes = scopeAgentTypes[scopeKey]
      if (allowedTypes && agentType && !allowedTypes.includes(agentType)) return false
      return true
    }).length
  }

  setFilteredRadioEnabled(enabled) {
    if (!this.hasScopeTypeSelectTarget) return
    const filteredRadio = this.scopeTypeSelectTarget.querySelector(
      'input[name="permission_policy[scope_type]"][value="filtered"]'
    )
    if (filteredRadio) {
      filteredRadio.disabled = !enabled
      if (!enabled && filteredRadio.checked) {
        this.scopeTypeValue = 'none'
        this.toggleScopeFilters()
      }
    }
  }

  toggleScopeFilters(event = null, selectedKeys = [], preselectedConfigValues = {}) {
    const scopeType = this.scopeTypeValue

    if (scopeType === 'filtered') {
      this.scopeFiltersTarget.classList.remove('hidden')
      this.loadScopes(selectedKeys, preselectedConfigValues)
    } else {
      this.scopeFiltersTarget.classList.add('hidden')
      this.scopeSelectTarget.innerHTML = ''
    }
  }

  loadScopes(selectedKeys = [], preselectedConfigValues = {}) {
    if (!this.selectedModel) return

    // Clear existing scopes
    this.scopeSelectTarget.innerHTML = ''

    // Filter scopes by selected parent's agent type
    const agentType = this.selectedParentAgentType
    const scopeAgentTypes = this.selectedModel.scope_agent_types || {}

    // Add scope options as checkboxes for multi-select
    this.selectedModel.scopes.forEach((scopeKey) => {
      // Skip scopes that don't apply to the selected agent type
      const allowedTypes = scopeAgentTypes[scopeKey]
      if (allowedTypes && agentType && !allowedTypes.includes(agentType)) return

      const isConfigurable = this.selectedModel.configurable_scopes?.[scopeKey]

      const div = document.createElement('div')
      div.className = 'p-3 bg-white rounded-lg border border-gray-200 scope-checkbox-item'

      // Checkbox row
      const checkboxRow = document.createElement('div')
      checkboxRow.className = 'flex items-start'

      const checkbox = document.createElement('input')
      checkbox.type = 'checkbox'
      checkbox.name = 'permission_policy[scope_keys][]'
      checkbox.value = scopeKey
      checkbox.id = `scope_${scopeKey}`
      checkbox.className = 'h-4 w-4 border-gray-300 rounded mt-1'
      checkbox.style.cssText = 'accent-color: #FFDE45;'

      // Pre-select if in selectedKeys
      if (selectedKeys.includes(scopeKey)) {
        checkbox.checked = true
      }

      const labelWrapper = document.createElement('label')
      labelWrapper.htmlFor = `scope_${scopeKey}`
      labelWrapper.className = 'ml-3 cursor-pointer flex-1'

      const scopeInfo = this.selectedModel.scope_descriptions?.[scopeKey] || {}

      const labelTitle = document.createElement('div')
      labelTitle.className = 'text-sm font-medium text-gray-900'
      labelTitle.textContent = scopeInfo.label || scopeKey

      const labelDesc = document.createElement('div')
      labelDesc.className = 'text-xs text-gray-500 mt-1'
      labelDesc.textContent = scopeInfo.description || ''

      labelWrapper.appendChild(labelTitle)
      labelWrapper.appendChild(labelDesc)

      checkboxRow.appendChild(checkbox)
      checkboxRow.appendChild(labelWrapper)
      div.appendChild(checkboxRow)

      // Add configurable options container (hidden until checkbox is checked)
      if (isConfigurable) {
        const optionsContainer = document.createElement('div')
        optionsContainer.id = `configurable_options_${scopeKey}`
        optionsContainer.className = 'ml-7 mt-2 hidden'
        optionsContainer.innerHTML =
          '<div class="text-xs text-gray-400 py-2">Carregando opções...</div>'

        div.appendChild(optionsContainer)

        // Toggle options when checkbox changes
        checkbox.addEventListener('change', () => {
          if (checkbox.checked) {
            optionsContainer.classList.remove('hidden')
            this.loadConfigurableOptions(
              scopeKey,
              optionsContainer,
              preselectedConfigValues[scopeKey] || []
            )
          } else {
            optionsContainer.classList.add('hidden')
            optionsContainer.innerHTML = ''
          }
        })

        // If pre-selected, load options immediately
        if (checkbox.checked) {
          optionsContainer.classList.remove('hidden')
          this.loadConfigurableOptions(
            scopeKey,
            optionsContainer,
            preselectedConfigValues[scopeKey] || []
          )
        }
      }

      this.scopeSelectTarget.appendChild(div)
    })
  }

  async loadConfigurableOptions(scopeKey, container, preselectedValues = []) {
    const resourceType = this.resourceSelectTarget.value
    if (!resourceType) return

    try {
      const url = new URL(this.formTarget.action, window.location.origin)
      url.pathname = url.pathname.replace(/\/permissions.*$/, '/permissions/configurable_scope_options')
      url.searchParams.set('resource_type', resourceType)
      url.searchParams.set('scope_key', scopeKey)
      preselectedValues.forEach((value) => url.searchParams.append('selected[]', value))

      const response = await fetch(url.toString(), {
        headers: { Accept: 'application/json', 'X-CSRF-Token': this.csrfToken() },
      })

      if (!response.ok) throw new Error('Failed to load options')

      const options = await response.json()
      this.renderConfigurableOptions(scopeKey, container, options, preselectedValues)
    } catch (error) {
      container.innerHTML =
        '<div class="text-xs text-red-500 py-2">Erro ao carregar opções</div>'
    }
  }

  renderConfigurableOptions(scopeKey, container, options, preselectedValues = []) {
    const freeText = this.selectedModel?.configurable_scopes?.[scopeKey]?.free_text

    if (options.length === 0 && !freeText) {
      container.innerHTML =
        '<div class="text-xs text-gray-400 py-2">Nenhuma opção disponível</div>'
      return
    }

    container.innerHTML = ''

    const wrapper = document.createElement('div')
    wrapper.className = 'space-y-1.5 max-h-72 overflow-y-auto'

    options.forEach((option) => {
      wrapper.appendChild(
        this.configurableOptionItem(scopeKey, option, preselectedValues.includes(option.id))
      )
    })

    if (options.length > 8) container.appendChild(this.configurableOptionsSearch(wrapper))
    if (freeText) container.appendChild(this.configurableFreeTextInput(scopeKey, wrapper, freeText))
    container.appendChild(wrapper)
  }

  configurableOptionItem(scopeKey, option, checked) {
    const optionDiv = document.createElement('label')
    optionDiv.className =
      'flex items-center gap-2 px-2.5 py-1.5 rounded-md hover:bg-yellow-50 cursor-pointer transition-colors'

    const cb = document.createElement('input')
    cb.type = 'checkbox'
    cb.name = `configurable_scope_values[${scopeKey}][]`
    cb.value = option.id
    cb.checked = checked
    cb.className = 'h-3.5 w-3.5 border-gray-300 rounded'
    cb.style.cssText = 'accent-color: #FFDE45;'

    const label = document.createElement('span')
    label.className = 'text-sm text-gray-700'
    label.textContent = option.label

    optionDiv.appendChild(cb)
    optionDiv.appendChild(label)
    return optionDiv
  }

  configurableOptionsSearch(wrapper) {
    const input = document.createElement('input')
    input.type = 'search'
    input.placeholder = 'Buscar…'
    input.className = 'input input-sm input-bordered w-full mb-2'
    input.addEventListener('input', () => {
      const term = input.value.trim().toLowerCase()
      wrapper.querySelectorAll('label').forEach((item) => {
        item.classList.toggle('hidden', !item.textContent.toLowerCase().includes(term))
      })
    })
    return input
  }

  configurableFreeTextInput(scopeKey, wrapper, freeText) {
    const row = document.createElement('div')
    row.className = 'flex gap-2 mb-2'

    const input = document.createElement('input')
    input.type = 'text'
    input.placeholder = freeText.placeholder || ''
    input.className = 'input input-sm input-bordered flex-1'

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'btn btn-sm'
    button.textContent = 'Adicionar'

    const add = () => {
      const text = input.value.trim()
      if (text.length < (freeText.min_length || 1)) return

      const option = { id: `${freeText.value_prefix}${text}`, label: `${freeText.label_prefix}${text}` }
      const duplicate = [...wrapper.querySelectorAll('input[type=checkbox]')].some((cb) => cb.value === option.id)
      if (!duplicate) wrapper.prepend(this.configurableOptionItem(scopeKey, option, true))
      input.value = ''
    }

    button.addEventListener('click', add)
    input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return
      event.preventDefault()
      add()
    })

    row.appendChild(input)
    row.appendChild(button)
    return row
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  getScopeDescription(modelName, scopeKey) {
    // This method is now deprecated - scope descriptions come from selectedModel.scope_descriptions
    const modelConfig = this.modelsConfig[modelName]
    return modelConfig?.scope_descriptions?.[scopeKey]?.description || ''
  }

  resetForm() {
    this.selectedModel = null
    this.selectedAction = null
    this.editingPermissionId = null

    // Clear permission ID hidden field
    if (this.hasPermissionIdTarget) {
      this.permissionIdTarget.value = ''
    }

    // Reset applies-to to default (all members)
    if (this.hasAppliesToSelectTarget) {
      this.appliesToSelectTarget.value = 'all_members'
    }

    // Reset form action to create URL
    if (this.hasFormTarget) {
      const form = this.formTarget
      const baseUrl = form.action.replace(/\/permissions.*$/, '')
      form.action = `${baseUrl}/permissions`

      // Remove method override if exists
      const methodInput = form.querySelector('input[name="_method"]')
      if (methodInput) {
        methodInput.remove()
      }
    }

    // Hide and reset parent selector
    this.hideParentSelector()

    // Hide resource description
    this.hideResourceDescription()

    // Show scope selection by default (will be hidden if needed when resource is selected)
    this.showScopeSelection()

    // Reset all selects
    if (this.hasResourceSelectTarget) this.resourceSelectTarget.selectedIndex = 0
    if (this.hasActionSelectTarget)
      this.actionSelectTarget.innerHTML = '<option value="">Selecione a Ação</option>'
    this.scopeTypeValue = 'none'
    if (this.hasScopeFiltersTarget) this.scopeFiltersTarget.classList.add('hidden')
    if (this.hasScopeSelectTarget) {
      // Re-enable all radio buttons
      const radioButtons = this.scopeSelectTarget.querySelectorAll('input[type="radio"]')
      radioButtons.forEach((radio) => (radio.disabled = false))
      this.scopeSelectTarget.innerHTML = ''
    }
  }

  translateAction(action) {
    return this.actionLabels[action] || action
  }

  translateScope(modelName, scopeKey) {
    // This method is now deprecated - scope labels come from selectedModel.scope_descriptions
    const modelConfig = this.modelsConfig[modelName]
    return modelConfig?.scope_descriptions?.[scopeKey]?.label || scopeKey
  }
}
